import type { HackcalEvent } from "./types";

export const DEFAULT_DURATION_MINUTES = 180;
const TIMEZONE = "Europe/Berlin";

export interface CalendarEventInput {
  uid: string;
  dateKey: string;
  event: HackcalEvent;
}

/** hackcal only gives a start time string like "19:00"; combined with the day's date key this is the event start. */
export function parseStart(dateKey: string, datestr: string): Date | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(datestr.trim());
  if (!match) return null;
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day, Number(match[1]), Number(match[2]));
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatIcsDate(date: Date): string {
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}00`;
}

/**
 * DTSTAMP MUST be in UTC (RFC 5545 §3.8.7.2, the "Z"-suffixed form) - unlike DTSTART/DTEND,
 * which pair a naive wall-clock time with a TZID. `formatIcsDate` reads local getters
 * (getHours/getMinutes, in whatever timezone the browser/runtime is in), so appending a
 * literal "Z" to its output mislabels local time as UTC - off by 1-2h for any Central
 * European visitor, depending on DST. This formats with the UTC getters instead.
 */
function formatIcsDateUtc(date: Date): string {
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
}

function lastSundayOfMonth(year: number, month0: number): number {
  const lastDay = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const weekday = new Date(Date.UTC(year, month0, lastDay)).getUTCDay();
  return lastDay - weekday;
}

/** EU summer time runs from the last Sunday of March to the last Sunday of October (calendar-date accuracy is enough since events are always in the evening, never near the 1-3am transition edge). */
function isBerlinSummerTime(year: number, month0: number, day: number): boolean {
  const dstStart = Date.UTC(year, 2, lastSundayOfMonth(year, 2));
  const dstEnd = Date.UTC(year, 9, lastSundayOfMonth(year, 9));
  const current = Date.UTC(year, month0, day);
  return current >= dstStart && current < dstEnd;
}

/**
 * Formats a Date (built from hackcal's naive local wall-clock components, see `parseStart`)
 * as an ISO 8601 string carrying the correct Europe/Berlin UTC offset for that calendar date
 * — needed for schema.org Event `startDate`/`endDate`, which (unlike ICS's TZID) must embed
 * the offset directly.
 */
export function toBerlinIsoString(date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const offset = isBerlinSummerTime(year, month, day) ? "+02:00" : "+01:00";
  return `${year}-${pad(month + 1)}-${pad(day)}T${pad(date.getHours())}:${pad(date.getMinutes())}:00${offset}`;
}

function escapeIcsText(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

const utf8Encoder = new TextEncoder();

function utf8ByteLength(text: string): number {
  return utf8Encoder.encode(text).length;
}

/**
 * ICS lines must not exceed 75 octets; continuation lines start with a single space
 * (RFC 5545 §3.1). "Octets" means UTF-8 bytes, not JS string length - German umlauts
 * (ä/ö/ü/ß, unavoidable in event text on this site) and emoji take 2-4 UTF-8 bytes
 * but only one JS UTF-16 code unit (or, for emoji, a surrogate pair's two), so folding
 * by `.length`/`.slice()` both produced over-long lines for any non-ASCII text and
 * risked slicing a surrogate pair in half. Iterating with `for...of` (which steps by
 * Unicode code point, not UTF-16 code unit) keeps every fold point on a whole-character
 * boundary; the 73-byte budget below (rather than the full 75) leaves the same safety
 * margin the previous, character-counting version used.
 */
function foldIcsLine(line: string): string {
  const CHUNK_BYTES = 73;
  if (utf8ByteLength(line) <= CHUNK_BYTES) return line;

  const lines: string[] = [];
  let current = "";
  let currentBytes = 0;

  for (const ch of line) {
    const chBytes = utf8ByteLength(ch);
    if (currentBytes + chBytes > CHUNK_BYTES) {
      lines.push(current);
      current = "";
      currentBytes = 0;
    }
    current += ch;
    currentBytes += chBytes;
  }
  lines.push(current);

  return lines.map((l, i) => (i === 0 ? l : ` ${l}`)).join("\r\n");
}

export function buildGoogleCalendarUrl({ dateKey, event }: CalendarEventInput): string | null {
  const start = parseStart(dateKey, event.datestr);
  if (!start) return null;
  const end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60_000);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.summary,
    dates: `${formatIcsDate(start)}/${formatIcsDate(end)}`,
    ctz: TIMEZONE,
  });
  if (event.location) params.set("location", event.location);
  if (event.description) params.set("details", event.description);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Standard Europe/Berlin DST rules (last Sunday of March/October), so calendar apps
// that don't already know the TZID can still resolve the correct offset.
const VTIMEZONE_EUROPE_BERLIN = [
  "BEGIN:VTIMEZONE",
  "TZID:Europe/Berlin",
  "BEGIN:DAYLIGHT",
  "TZOFFSETFROM:+0100",
  "TZOFFSETTO:+0200",
  "TZNAME:CEST",
  "DTSTART:19700329T020000",
  "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU",
  "END:DAYLIGHT",
  "BEGIN:STANDARD",
  "TZOFFSETFROM:+0200",
  "TZOFFSETTO:+0100",
  "TZNAME:CET",
  "DTSTART:19701025T030000",
  "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU",
  "END:STANDARD",
  "END:VTIMEZONE",
];

function buildVeventLines({ uid, dateKey, event }: CalendarEventInput, now: Date): string[] | null {
  const start = parseStart(dateKey, event.datestr);
  if (!start) return null;
  const end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60_000);

  const lines = [
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatIcsDateUtc(now)}`,
    `DTSTART;TZID=${TIMEZONE}:${formatIcsDate(start)}`,
    `DTEND;TZID=${TIMEZONE}:${formatIcsDate(end)}`,
    `SUMMARY:${escapeIcsText(event.summary)}`,
  ];
  if (event.location) lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  if (event.description) lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  lines.push("END:VEVENT");
  return lines;
}

export function buildIcsCalendar(events: CalendarEventInput[], now: Date = new Date()): string {
  const veventLines = events.flatMap((input) => buildVeventLines(input, now) ?? []);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Hackwerk Aalen//Termine//DE",
    "CALSCALE:GREGORIAN",
    ...VTIMEZONE_EUROPE_BERLIN,
    ...veventLines,
    "END:VCALENDAR",
  ];

  return lines.map(foldIcsLine).join("\r\n");
}

export function downloadIcsFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
